import type { CurrencyInfo } from "./currency";

/**
 * Formats a price from minor units into a displayable string, using provided
 * currency settings.
 * @param price - The price string in minor units (e.g., "1500023").
 * @param currencyInfo - An object containing currency formatting rules.
 * @returns The fully formatted price string (e.g., "₺15.000,23₺").
 */
export const formatPrice = (
  price: string | undefined | null,
  currencyInfo: CurrencyInfo = {}
): string => {
  // Get currency settings from the provided object with sensible fallbacks.
  const {
    currencyMinorUnit: minorUnit = 2,
    currencyDecimalSeparator: decimalSeparator = ",",
    currencyThousandSeparator: thousandSeparator = ".",
    currencyPrefix: prefix = "",
    currencySuffix: suffix = "",
  } = currencyInfo;

  let numericPrice: number;

  if (!price) {
    numericPrice = 0;
  } else {
    const divisor = Math.pow(10, minorUnit);
    const parsedPrice = parseFloat(price) / divisor;
    numericPrice = isNaN(parsedPrice) ? 0 : parsedPrice;
  }

  // Format the number to a string with a dot as a temporary decimal separator.
  const fixedPriceString = numericPrice.toFixed(minorUnit);

  const parts = fixedPriceString.split(".");
  let integerPart = parts[0];
  const decimalPart = parts.length > 1 ? parts[1] : "";

  // Insert the thousand separator.
  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);

  // Rejoin with the correct decimal separator and add prefix/suffix.
  const formattedNumber = `${integerPart}${decimalSeparator}${decimalPart}`;

  return `${prefix}${formattedNumber}${suffix}`;
};
